// ── LogisticsAdvancedTabs.tsx ─────────────────────────────────────────────────
// 2 advanced Logistics tabs — live data from NEXUS360_Production_Data Google Sheet
// Required tabs: logistics_geofencing | logistics_fuel_theft
// Sheet ID: 1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A (public Viewer)
//
// Usage in LogisticsIntelligencePage.tsx:
//   import { LogisticsAdvancedTabs } from './LogisticsAdvancedTabs';
//   <LogisticsAdvancedTabs />   ← just before the Footer div
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Truck, AlertTriangle, CheckCircle, MapPin, Clock, Shield,
  RefreshCw, Search, Filter, AlertCircle, DollarSign, Brain,
  ChevronDown, ChevronUp, Zap, Activity, BarChart2, Navigation,
  Eye, TrendingUp, Flame
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LT, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, ComposedChart, Line, ScatterChart, Scatter,
  ZAxis, ReferenceLine, PieChart, Pie
} from 'recharts';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Config ────────────────────────────────────────────────────────────────────
const MAIN_SHEET = '1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A';
const sheetUrl = (tab: string) =>
  `https://docs.google.com/spreadsheets/d/${MAIN_SHEET}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(tab)}`;
const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899'];

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCsv(text: string): Record<string,string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g,'').trim());
  return lines.slice(1).map(line => {
    const vals: string[] = []; let cur = '', inQ = false;
    for (const c of line) {
      if (c==='"'){inQ=!inQ;continue;} if(c===','&&!inQ){vals.push(cur);cur='';continue;} cur+=c;
    }
    vals.push(cur);
    const row: Record<string,string> = {};
    headers.forEach((h,i) => { row[h]=(vals[i]||'').trim(); });
    return row;
  });
}

function useSheet<T>(tab: string, transform: (rows: Record<string,string>[]) => T[]) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetch_ = useCallback(async () => {
    setLoading(true); setError(false);
    try { const r = await fetch(sheetUrl(tab)); setData(transform(parseCsv(await r.text()))); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

const n = (v: any, fb=0) => { if(v===null||v===undefined||v==='') return fb; const x=Number(String(v).replace(',','.')); return isNaN(x)?fb:x; };
const fmtF = (v: number) => v>=1e6?(v/1e6).toFixed(1)+'M F':v>=1e3?(v/1e3).toFixed(0)+'K F':Math.round(v)+' F';

// ── Shared UI ─────────────────────────────────────────────────────────────────
const NexTip = ({active,payload,label}:any) => {
  if(!active||!payload?.length) return null;
  return (
    <motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
      className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
      <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
      {payload.map((p:any,i:number) => (
        <div key={i} className="flex justify-between gap-3 py-0.5">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div>
          <span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span>
        </div>
      ))}
    </motion.div>
  );
};

const Card = ({icon:Icon,title,children,accent='text-blue-400',className='',action}:any) => (
  <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.07)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    className="bg-slate-800/80">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white flex-1">{title}</span>{action}
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const KpiCard = ({icon:Icon,label,val,suffix='',dec=0,color,bg,border,alert=false}:any) => {
  const glow = color.includes('green')?'rgba(16,185,129,.2)':color.includes('red')?'rgba(239,68,68,.2)':color.includes('orange')?'rgba(249,115,22,.2)':color.includes('purple')?'rgba(168,85,247,.2)':'rgba(59,130,246,.2)';
  return (
    <motion.div whileHover={{scale:1.04,boxShadow:`0 0 16px ${glow}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-3 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,.85) 0%,rgba(30,41,59,.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-40"
        style={{background:`linear-gradient(90deg,transparent,${color.includes('green')?'#10b981':color.includes('red')?'#ef4444':color.includes('orange')?'#f97316':color.includes('purple')?'#a855f7':'#3b82f6'},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 ${alert&&val>0?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-lg leading-none ${color}`}>
          <CountUp end={val} duration={1.5} decimals={dec} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&val>0&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

const PBar = ({value,color='bg-blue-500',delay=0}:{value:number;color?:string;delay?:number}) => (
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{width:0}} animate={{width:`${Math.min(100,value)}%`}}
      transition={{duration:1,ease:'easeOut',delay}}/>
  </div>
);

const LoadingBlock = () => (
  <div className="space-y-3 p-2">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={40} className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={20} count={5}/>
  </div>
);

const ErrorBlock = ({onRetry}:{onRetry:()=>void}) => (
  <div className="flex flex-col items-center gap-3 py-10 text-slate-400">
    <AlertCircle className="w-8 h-8 text-red-400"/>
    <p className="text-sm">Impossible de charger depuis Google Sheets.</p>
    <button onClick={onRetry} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs hover:bg-blue-500/30">
      <RefreshCw className="w-3.5 h-3.5"/>Réessayer
    </button>
  </div>
);

// ── Map resizer ───────────────────────────────────────────────────────────────
const MapResizer = () => { const map = useMap(); useEffect(()=>{setTimeout(()=>map.invalidateSize(),300);},[map]); return null; };

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — VEHICLE GEOFENCING & UNAUTHORIZED MOVEMENT
// ══════════════════════════════════════════════════════════════════════════════
interface TripRow {
  trip_id:string; vehicle_id:string; vehicle_registration:string; vehicle_type:string;
  region:string; unit:string; assigned_officer:string; assigned_zone:string;
  assigned_zone_lat:number; assigned_zone_lon:number;
  trip_date:string; trip_start_time:string; trip_end_time:string; trip_duration_minutes:number;
  departure_lat:number; departure_lon:number; arrival_lat:number; arrival_lon:number;
  current_lat:number; current_lon:number; distance_km:number;
  inside_assigned_zone:string; after_hours:string; dispatch_authorized:string;
  violation_type:string; alert_level:string; alert_sent:string; audit_logged:string;
  commander_notified:string; trip_purpose:string; vehicle_status:string;
}
const transformTrip = (rows:Record<string,string>[]): TripRow[] =>
  rows.map(r=>({...r,
    assigned_zone_lat:n(r.assigned_zone_lat),assigned_zone_lon:n(r.assigned_zone_lon),
    departure_lat:n(r.departure_lat),departure_lon:n(r.departure_lon),
    arrival_lat:n(r.arrival_lat),arrival_lon:n(r.arrival_lon),
    current_lat:n(r.current_lat),current_lon:n(r.current_lon),
    distance_km:n(r.distance_km),trip_duration_minutes:n(r.trip_duration_minutes)} as TripRow));

const GeofencingTab = () => {
  const {data,loading,error,refetch} = useSheet('logistics_geofencing',transformTrip);
  const [alertF,setAlertF] = useState('');
  const [regionF,setRegionF] = useState('');
  const [search,setSearch] = useState('');
  const [selVehicle,setSelVehicle] = useState<TripRow|null>(null);

  const filtered = useMemo(()=>data.filter(r=>
    (!alertF||r.alert_level===alertF)&&
    (!regionF||r.region===regionF)&&
    (!search||[r.vehicle_registration,r.assigned_officer,r.assigned_zone].join(' ').toLowerCase().includes(search.toLowerCase()))
  ),[data,alertF,regionF,search]);

  const actifs    = data.filter(r=>r.vehicle_status==='Actif'||r.vehicle_status==='En mission').length;
  const hors_zone = data.filter(r=>r.inside_assigned_zone==='NON').length;
  const unauth    = data.filter(r=>r.dispatch_authorized==='NON').length;
  const critiques = data.filter(r=>r.alert_level==='CRITIQUE').length;
  const regions   = [...new Set(data.map(r=>r.region))].sort();

  // Alert distribution
  const alertStats = useMemo(()=>['CRITIQUE','HAUTE','MOYENNE','AUCUNE'].map((a,i)=>({
    alert:a, count:filtered.filter(r=>r.alert_level===a).length,
    fill:['#ef4444','#f97316','#eab308','#10b981'][i]
  })),[filtered]);

  // Violations by type
  const violStats = useMemo(()=>{
    const m:Record<string,number>={};
    filtered.filter(r=>r.violation_type).forEach(r=>{m[r.violation_type]=(m[r.violation_type]||0)+1;});
    return Object.entries(m).map(([name,count])=>({name:name.slice(0,32),count})).sort((a,b)=>b.count-a.count);
  },[filtered]);

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Truck}         label="Véhicules actifs"       val={actifs}    color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KpiCard icon={AlertTriangle}  label="Hors zone assignée"    val={hors_zone} color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30" alert/>
        <KpiCard icon={Shield}         label="Trajets non autorisés" val={unauth}    color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
        <KpiCard icon={Zap}            label="Alertes CRITIQUES"     val={critiques} color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30" alert/>
      </div>

      {/* Side-by-side: violations table (1/3) + map (2/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

        {/* LEFT: Violations CRITIQUE table (1/3) */}
        <div className="lg:col-span-1 rounded-xl border border-red-500/30 overflow-hidden"
          style={{background:'linear-gradient(135deg,rgba(127,29,29,.15) 0%,rgba(15,23,42,.9) 100%)'}}>
          <div className="px-3 py-2.5 border-b border-red-500/20 flex items-center gap-2">
            <motion.div animate={{rotate:[0,-8,8,-8,0]}} transition={{duration:.5,repeat:Infinity,repeatDelay:2}}>
              <AlertTriangle className="w-4 h-4 text-red-400"/>
            </motion.div>
            <div className="flex-1 min-w-0">
              <p className="text-red-300 font-bold text-xs">⛔ {critiques} VIOLATION{critiques>1?'S':''} CRITIQUE{critiques>1?'S':''}</p>
              <p className="text-red-400/60 text-xs leading-tight">Centre de commandement notifié · Piste d'audit créée</p>
            </div>
            <motion.div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"
              animate={{opacity:[1,.1,1],scale:[1,1.6,1]}} transition={{duration:.9,repeat:Infinity}}/>
          </div>
          {/* Violations list */}
          <div className="overflow-y-auto" style={{maxHeight:'580px'}}>
            {critiques===0?(
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <span className="text-3xl">✅</span>
                <p className="text-green-400 text-xs font-medium">Aucune violation critique</p>
              </div>
            ):(
              <div className="divide-y divide-red-500/10">
                {data.filter(r=>r.alert_level==='CRITIQUE').map((r,i)=>(
                  <motion.div key={r.trip_id} initial={{opacity:0,x:-6}} animate={{opacity:1,x:0}}
                    transition={{delay:i*.06}}
                    className="px-3 py-2.5 hover:bg-red-500/5 transition-colors cursor-pointer"
                    onClick={()=>setSelVehicle(r===selVehicle?null:r)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-bold text-xs">{r.vehicle_registration}</span>
                      <motion.span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 font-bold"
                        animate={{opacity:[1,.6,1]}} transition={{duration:1.2,repeat:Infinity,delay:i*.15}}>
                        CRITIQUE
                      </motion.span>
                    </div>
                    <p className="text-slate-400 text-xs leading-tight mb-1">{r.vehicle_type}</p>
                    <p className="text-red-300 text-xs leading-tight font-medium mb-1">⚠ {r.violation_type}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5"/>{r.assigned_zone}</span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-3 gap-1 text-xs">
                      {[
                        ['Dans zone', r.inside_assigned_zone, r.inside_assigned_zone==='OUI'?'text-green-400':'text-red-400'],
                        ['Autorisé',  r.dispatch_authorized,  r.dispatch_authorized==='OUI'?'text-green-400':'text-red-400'],
                        ['Cmd. notif.',r.commander_notified,  r.commander_notified==='OUI'?'text-green-400':'text-orange-400'],
                      ].map(([k,v,c])=>(
                        <div key={k as string} className="bg-slate-800/60 rounded px-1 py-0.5 text-center">
                          <p className="text-slate-600" style={{fontSize:'7px'}}>{k as string}</p>
                          <p className={`font-bold ${c as string}`} style={{fontSize:'8px'}}>{v as string}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Fleet Map (2/3) */}
        <div className="lg:col-span-2">
          <Card icon={MapPin} title="Carte flotte temps réel — Positions GPS · Zones assignées · Violations" accent="text-green-400">
            <div className="rounded-xl overflow-hidden border border-blue-900/30" style={{height:'580px'}}>
          <MapContainer center={[5.5,12.3]} zoom={6}
            style={{width:'100%',height:'100%',background:'#0f172a'}}
            attributionControl={false} scrollWheelZoom>
            <MapResizer/>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" subdomains="abcd" maxZoom={19}/>

            {/* Assigned zone markers (faded circles) */}
            {[...new Set(filtered.map(r=>r.assigned_zone))].map(zone=>{
              const trips = filtered.filter(r=>r.assigned_zone===zone);
              if(!trips.length) return null;
              const t = trips[0];
              return (
                <CircleMarker key={'zone-'+zone}
                  center={[t.assigned_zone_lat,t.assigned_zone_lon]}
                  radius={32} fillColor="#3b82f6" color="#3b82f6"
                  fillOpacity={0.08} weight={1.5} dashArray="6 4">
                  <LT permanent={false} direction="top">
                    <div className="text-xs font-bold">Zone: {zone}</div>
                  </LT>
                </CircleMarker>
              );
            })}

            {/* Vehicle current positions */}
            {filtered.map((r,i)=>{
              const color = r.alert_level==='CRITIQUE'?'#ef4444':r.alert_level==='HAUTE'?'#f97316':r.alert_level==='MOYENNE'?'#eab308':'#10b981';
              const isViol = r.inside_assigned_zone==='NON'||r.dispatch_authorized==='NON';
              return (
                <React.Fragment key={r.trip_id}>
                  {/* Route line dep→arrival */}
                  {isViol&&<Polyline
                    positions={[[r.departure_lat,r.departure_lon],[r.current_lat,r.current_lon]]}
                    color={color} weight={2} dashArray="4 3" opacity={0.7}/>}
                  {/* Vehicle dot */}
                  <CircleMarker center={[r.current_lat,r.current_lon]}
                    radius={isViol?9:6}
                    fillColor={color} color={isViol?color:'#0f172a'}
                    fillOpacity={0.9} weight={isViol?2:1}
                    eventHandlers={{click:()=>setSelVehicle(r)}}>
                    <LT direction="top" permanent={false}>
                      <div style={{minWidth:'160px',background:'rgba(15,23,42,.95)',border:`1px solid ${color}`,borderRadius:'8px',padding:'8px',color:'white',fontSize:'11px'}}>
                        <p style={{fontWeight:'bold',marginBottom:'4px',color}}>{r.vehicle_registration} — {r.vehicle_type}</p>
                        <p>Zone assignée: <span style={{color:'#93c5fd'}}>{r.assigned_zone}</span></p>
                        <p>Officier: {r.assigned_officer}</p>
                        <p>Unité: {r.unit}</p>
                        {r.violation_type&&<p style={{color:'#fca5a5',fontWeight:'bold',marginTop:'4px'}}>⚠ {r.violation_type}</p>}
                        <p style={{color,fontWeight:'bold'}}>Alerte: {r.alert_level}</p>
                      </div>
                    </LT>
                  </CircleMarker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        </div>
        <div className="flex gap-4 mt-2 text-xs text-slate-500 flex-wrap">
          {[['#ef4444','CRITIQUE'],['#f97316','HAUTE'],['#eab308','MOYENNE'],['#10b981','OK'],['#3b82f6','Zone assignée (cercle)']].map(([c,l])=>(
            <span key={l} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full inline-block" style={{background:c}}/>
              {l}
            </span>
          ))}
        </div>
      </Card>
        </div>{/* end map 2/3 */}
      </div>{/* end grid */}

      {/* Vehicle detail panel */}
      <AnimatePresence>
        {selVehicle&&(
          <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}}
            className="rounded-xl border border-orange-500/40 p-4 relative overflow-hidden"
            style={{background:'linear-gradient(135deg,rgba(154,52,18,.15) 0%,rgba(15,23,42,.9) 100%)'}}>
            <button onClick={()=>setSelVehicle(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white">✕</button>
            <div className="flex items-center gap-3 mb-3">
              <motion.div whileHover={{rotate:15}} className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-orange-400"/>
              </motion.div>
              <div>
                <p className="text-white font-bold">{selVehicle.vehicle_registration} — {selVehicle.vehicle_type}</p>
                <p className="text-slate-400 text-xs">{selVehicle.assigned_officer} · {selVehicle.unit} · {selVehicle.region}</p>
              </div>
              <motion.span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold border ${
                selVehicle.alert_level==='CRITIQUE'?'bg-red-500/20 border-red-500/40 text-red-300':
                selVehicle.alert_level==='HAUTE'?'bg-orange-500/20 border-orange-500/40 text-orange-300':
                selVehicle.alert_level==='MOYENNE'?'bg-yellow-500/20 border-yellow-500/40 text-yellow-300':
                'bg-green-500/20 border-green-500/40 text-green-300'}`}
                animate={selVehicle.alert_level==='CRITIQUE'?{opacity:[1,.6,1]}:{}} transition={{duration:1,repeat:Infinity}}>
                {selVehicle.alert_level}
              </motion.span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {[
                ['Zone assignée',selVehicle.assigned_zone,'text-blue-300'],
                ['Dans la zone',selVehicle.inside_assigned_zone,selVehicle.inside_assigned_zone==='OUI'?'text-green-400':'text-red-400'],
                ['Hors horaires',selVehicle.after_hours,selVehicle.after_hours==='NON'?'text-green-400':'text-red-400'],
                ['Autorisé',selVehicle.dispatch_authorized,selVehicle.dispatch_authorized==='OUI'?'text-green-400':'text-red-400'],
                ['Distance',selVehicle.distance_km+'km','text-slate-300'],
                ['Durée',selVehicle.trip_duration_minutes+'min','text-slate-300'],
                ['Objectif',selVehicle.trip_purpose,'text-slate-300'],
                ['Commandant notifié',selVehicle.commander_notified,selVehicle.commander_notified==='OUI'?'text-green-400':'text-orange-400'],
              ].map(([k,v,c])=>(
                <div key={k} className="bg-slate-700/40 rounded-lg p-2">
                  <p className="text-slate-500" style={{fontSize:'9px'}}>{k}</p>
                  <p className={`font-bold text-xs ${c}`}>{v}</p>
                </div>
              ))}
            </div>
            {selVehicle.violation_type&&(
              <motion.div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-bold"
                animate={{opacity:[1,.7,1]}} transition={{duration:1.5,repeat:Infinity}}>
                ⚠ VIOLATION: {selVehicle.violation_type}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Alert distribution */}
        <Card icon={BarChart2} title="Distribution des alertes géofencing" accent="text-orange-400">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={alertStats} margin={{top:4,right:8,left:0,bottom:4}} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="alert" tick={{fill:'#94a3b8',fontSize:10}}/>
              <YAxis tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false}/>
              <Tooltip content={<NexTip/>}/>
              <Bar dataKey="count" name="Trajets" radius={[4,4,0,0]} isAnimationActive animationDuration={900}>
                {alertStats.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Violations by type */}
        <Card icon={AlertTriangle} title="Types de violations détectées" accent="text-red-400">
          {violStats.length===0?(
            <p className="text-green-400 text-sm text-center py-8">✓ Aucune violation détectée</p>
          ):(
            <div className="space-y-2 mt-2">
              {violStats.map((v,i)=>(
                <motion.div key={v.name} initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:i*.06}}
                  className="flex items-center gap-3">
                  <span className="text-slate-300 text-xs flex-1">{v.name}</span>
                  <span className="text-red-400 font-bold text-xs w-8 text-right">{v.count}</span>
                  <div className="w-24"><PBar value={v.count/Math.max(...violStats.map(x=>x.count))*100} color="bg-red-500" delay={i*.06}/></div>
                </motion.div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Immatriculation / zone…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:alertF,s:setAlertF,o:['CRITIQUE','HAUTE','MOYENNE','AUCUNE'],p:'Alerte'},
          {v:regionF,s:setRegionF,o:regions,p:'Région'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        {(search||alertF||regionF)&&<button onClick={()=>{setSearch('');setAlertF('');setRegionF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} trajets · Google Sheets live</span>
      </div>

      {/* Full table */}
      <Card icon={Navigation} title="Registre trajets & violations — Google Sheets live" accent="text-blue-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight:'400px'}}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['ID','Immat.','Type','Région','Unité','Officier','Zone assignée','Date','Heure','Dans zone','H. supp.','Autorisé','Distance','Violation','Alerte','Cmd. notifié'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r,i)=>(
                  <motion.tr key={r.trip_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                    whileHover={{backgroundColor:'rgba(59,130,246,.05)'}}
                    onClick={()=>setSelVehicle(r===selVehicle?null:r)}
                    className={`border-b border-blue-900/30 hover:bg-slate-700/50 transition-colors cursor-pointer ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.trip_id}</td>
                    <td className="px-3 py-2 text-white font-bold">{r.vehicle_registration}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.vehicle_type}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-36 truncate">{r.unit}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.assigned_officer}</td>
                    <td className="px-3 py-2 text-blue-300 whitespace-nowrap">{r.assigned_zone}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.trip_date}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.trip_start_time}</td>
                    <td className="px-3 py-2 text-center"><span className={r.inside_assigned_zone==='OUI'?'text-green-400 font-bold':'text-red-400 font-bold'}>{r.inside_assigned_zone==='OUI'?'✓':'✗'}</span></td>
                    <td className="px-3 py-2 text-center"><span className={r.after_hours==='NON'?'text-green-400':'text-red-400 font-bold'}>{r.after_hours}</span></td>
                    <td className="px-3 py-2 text-center"><span className={r.dispatch_authorized==='OUI'?'text-green-400 font-bold':'text-red-400 font-bold'}>{r.dispatch_authorized==='OUI'?'✓':'✗'}</span></td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.distance_km}km</td>
                    <td className="px-3 py-2 max-w-48 truncate text-xs text-orange-300">{r.violation_type||'—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                        r.alert_level==='CRITIQUE'?'bg-red-500/10 border-red-500/30 text-red-400':
                        r.alert_level==='HAUTE'?'bg-orange-500/10 border-orange-500/30 text-orange-400':
                        r.alert_level==='MOYENNE'?'bg-yellow-500/10 border-yellow-500/30 text-yellow-400':
                        'bg-green-500/10 border-green-500/30 text-green-400'}`}
                        animate={r.alert_level==='CRITIQUE'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                        {r.alert_level}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 text-center"><span className={r.commander_notified==='OUI'?'text-green-400':'text-slate-500'}>{r.commander_notified}</span></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — FUEL THEFT DETECTION (AI)
// ══════════════════════════════════════════════════════════════════════════════
interface FuelRow {
  transaction_id:string; vehicle_id:string; vehicle_registration:string;
  vehicle_type:string; region:string; unit:string; assigned_officer:string;
  fuel_date:string; fuel_station:string; fuel_card_id:string;
  litres_purchased:number; litres_expected_for_distance:number; distance_km_since_last_fill:number;
  consumption_actual_l_100km:number|string; consumption_standard_l_100km:number;
  consumption_variance_pct:number|string; tank_capacity_l:number;
  odometer_before:number; odometer_after:number; fuel_cost_fcfa:number;
  receipt_verified:string; gps_matches_station:string;
  anomaly_score_ml:number; anomaly_category:string; fraud_flag:string;
  predicted_theft_risk_14d:string; action_requise:string;
}
const transformFuel = (rows:Record<string,string>[]): FuelRow[] =>
  rows.map(r=>({...r,
    litres_purchased:n(r.litres_purchased),litres_expected_for_distance:n(r.litres_expected_for_distance),
    distance_km_since_last_fill:n(r.distance_km_since_last_fill),
    consumption_actual_l_100km:r.consumption_actual_l_100km==='N/A'?'N/A':n(r.consumption_actual_l_100km),
    consumption_standard_l_100km:n(r.consumption_standard_l_100km),
    consumption_variance_pct:r.consumption_variance_pct==='N/A'?'N/A':n(r.consumption_variance_pct),
    tank_capacity_l:n(r.tank_capacity_l),odometer_before:n(r.odometer_before),
    odometer_after:n(r.odometer_after),fuel_cost_fcfa:n(r.fuel_cost_fcfa),
    anomaly_score_ml:n(r.anomaly_score_ml)} as FuelRow));

const FuelTheftTab = () => {
  const {data,loading,error,refetch} = useSheet('logistics_fuel_theft',transformFuel);
  const [fraudF,setFraudF] = useState('');
  const [riskF,setRiskF] = useState('');
  const [search,setSearch] = useState('');
  const [sortDesc,setSortDesc] = useState(true);

  const filtered = useMemo(()=>[...data]
    .filter(r=>(!fraudF||r.fraud_flag===fraudF)&&(!riskF||r.predicted_theft_risk_14d===riskF)&&
      (!search||[r.vehicle_registration,r.assigned_officer,r.fuel_station].join(' ').toLowerCase().includes(search.toLowerCase())))
    .sort((a,b)=>sortDesc?b.anomaly_score_ml-a.anomaly_score_ml:a.anomaly_score_ml-b.anomaly_score_ml),
  [data,fraudF,riskF,search,sortDesc]);

  const confirmes  = data.filter(r=>r.fraud_flag==='CONFIRMÉ').length;
  const suspects   = data.filter(r=>r.fraud_flag==='SUSPECT').length;
  const totalCost  = data.filter(r=>r.fraud_flag!=='NON').reduce((s,r)=>s+r.fuel_cost_fcfa,0);
  const critRisk14 = data.filter(r=>r.predicted_theft_risk_14d==='CRITIQUE').length;

  // Anomaly by type
  const anomalyStats = useMemo(()=>{
    const m:Record<string,{count:number;avgScore:number}> = {};
    data.forEach(r=>{if(!m[r.anomaly_category])m[r.anomaly_category]={count:0,avgScore:0};
      m[r.anomaly_category].count++;m[r.anomaly_category].avgScore+=r.anomaly_score_ml;});
    return Object.entries(m).map(([name,d])=>({name:name.slice(0,28),count:d.count,avgScore:Math.round(d.avgScore/d.count)})).sort((a,b)=>b.count-a.count);
  },[data]);

  // Scatter: variance vs anomaly score
  const scatterData = useMemo(()=>data
    .filter(r=>r.consumption_variance_pct!=='N/A')
    .map(r=>({x:n(r.consumption_variance_pct),y:r.anomaly_score_ml,name:r.vehicle_registration,type:r.vehicle_type,
      fill:r.fraud_flag==='CONFIRMÉ'?'#ef4444':r.fraud_flag==='SUSPECT'?'#f97316':'#10b981'})),
  [data]);

  // Prediction 14d trend
  const risk14Data = ['CRITIQUE','ÉLEVÉ','FAIBLE'].map((risk,i)=>({
    risk, count:data.filter(r=>r.predicted_theft_risk_14d===risk).length,
    fill:['#ef4444','#f97316','#10b981'][i]
  }));

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-orange-500/30 p-3 flex items-center gap-3"
        style={{background:'linear-gradient(135deg,rgba(154,52,18,.15) 0%,rgba(15,23,42,.8) 100%)'}}>
        <Flame className="w-5 h-5 text-orange-400 flex-shrink-0"/>
        <div>
          <p className="text-white font-semibold text-sm">Moteur IA Détection Fraude Carburant — Modèle ML sur GPS + Transactions + Consommation standard + Maintenance</p>
          <p className="text-slate-400 text-xs">Scénarios: Surclaims · Achats sans déplacement · Siphonnage · Partage carte · Reçus falsifiés</p>
        </div>
        <div className="ml-auto text-right">
          <motion.p className="text-orange-300 font-black text-2xl" animate={{opacity:[1,.7,1]}} transition={{duration:2,repeat:Infinity}}>
            <CountUp end={confirmes+suspects} duration={1.5} preserveValue/>
          </motion.p>
          <p className="text-slate-400 text-xs">Anomalies détectées</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Flame}         label="Fraudes confirmées"      val={confirmes}  color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
        <KpiCard icon={AlertTriangle}  label="Cas suspects"            val={suspects}   color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30" alert/>
        <KpiCard icon={DollarSign}     label="Coût fraudes (M F)"      val={Math.round(totalCost/1e6*10)/10} dec={1} color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
        <KpiCard icon={Brain}          label="Risque critique 14j"     val={critRisk14} color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30" alert/>
      </div>

      {/* Critical fraud alert */}
      {confirmes > 0 && (
        <motion.div className="rounded-xl border border-red-500/40 p-3 flex items-start gap-3 relative overflow-hidden"
          style={{background:'linear-gradient(135deg,rgba(127,29,29,.2) 0%,rgba(15,23,42,.8) 100%)'}}>
          <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
            animate={{boxShadow:['0 0 0px rgba(239,68,68,0)','0 0 22px rgba(239,68,68,.2)','0 0 0px rgba(239,68,68,0)']}}
            transition={{duration:2,repeat:Infinity}}/>
          <motion.div animate={{scale:[1,1.2,1]}} transition={{duration:1,repeat:Infinity}}>
            <Flame className="w-5 h-5 text-red-400 flex-shrink-0"/>
          </motion.div>
          <div className="flex-1 relative z-10">
            <p className="text-red-300 font-bold text-sm mb-1">🔴 {confirmes} fraude(s) CONFIRMÉE(S) — Suspension carte + Enquête judiciaire requise</p>
            <div className="flex flex-wrap gap-2">
              {data.filter(r=>r.fraud_flag==='CONFIRMÉ').slice(0,5).map((r,i)=>(
                <motion.span key={i} className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30"
                  animate={{opacity:[1,.5,1]}} transition={{duration:1.5,repeat:Infinity,delay:i*.2}}>
                  {r.vehicle_registration} · {r.anomaly_category?.slice(0,25)} · Score: {r.anomaly_score_ml}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Scatter: variance vs ML score */}
        <Card icon={Brain} title="Variance consommation vs Score ML — Détection anomalies" accent="text-purple-400">
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{top:8,right:16,left:0,bottom:24}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" strokeOpacity={.6}/>
              <XAxis type="number" dataKey="x" name="Variance (%)" tick={{fill:'#94a3b8',fontSize:9}}
                label={{value:'Variance consommation (%)',position:'insideBottom',offset:-8,fill:'#64748b',fontSize:9}}/>
              <YAxis type="number" dataKey="y" name="Score ML" domain={[0,100]}
                tick={{fill:'#94a3b8',fontSize:9}} label={{value:'Score ML',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:9}}/>
              <ZAxis range={[30,120]}/>
              <Tooltip cursor={{stroke:'rgba(99,102,241,.3)',strokeWidth:1}} content={({active,payload}:any)=>{
                if(!active||!payload?.length) return null;
                const d=payload[0]?.payload;
                return (<div className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 text-xs backdrop-blur">
                  <p className="text-white font-bold mb-1">{d.name} — {d.type}</p>
                  <p className="text-orange-400">Variance: {d.x}%</p>
                  <p className="text-purple-400">Score ML: {d.y}</p>
                </div>);
              }}/>
              <ReferenceLine y={55} stroke="#f97316" strokeDasharray="4 2" label={{value:'Suspect',fill:'#f97316',fontSize:8,position:'insideTopRight'}}/>
              <ReferenceLine y={75} stroke="#ef4444" strokeDasharray="4 2" label={{value:'Confirmé',fill:'#ef4444',fontSize:8,position:'insideTopRight'}}/>
              <ReferenceLine x={30} stroke="#eab308" strokeDasharray="4 2" label={{value:'+30% var.',fill:'#eab308',fontSize:8,position:'insideTopLeft'}}/>
              <Scatter data={scatterData} shape={(props:any)=>{
                const{cx,cy,payload}=props;
                return(<g><circle cx={cx} cy={cy} r={7} fill={payload.fill} fillOpacity={.85} stroke="#0f172a" strokeWidth={1.5}/><circle cx={cx} cy={cy} r={3} fill="white" fillOpacity={.4}/></g>);
              }} isAnimationActive animationDuration={1200}/>
            </ScatterChart>
          </ResponsiveContainer>
        </Card>

        {/* Anomaly categories */}
        <Card icon={BarChart2} title="Catégories d'anomalies détectées" accent="text-red-400">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={anomalyStats} layout="vertical" margin={{top:4,right:40,left:8,bottom:4}} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false}/>
              <YAxis type="category" dataKey="name" tick={{fill:'#cbd5e1',fontSize:8}} width={170}/>
              <Tooltip content={<NexTip/>}/>
              <Bar dataKey="count" name="Transactions" radius={[0,4,4,0]} isAnimationActive animationDuration={900}>
                {anomalyStats.map((_,i)=><Cell key={i} fill={i===0?'#ef4444':i===1?'#f97316':i===2?'#eab308':i===3?'#a855f7':'#3b82f6'}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Predictive 14-day risk */}
      <Card icon={TrendingUp} title="Prédiction ML — Risque vol carburant dans 14 jours" accent="text-cyan-400">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {risk14Data.map((r,i)=>(
            <motion.div key={r.risk} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.1}}
              whileHover={{scale:1.03}}
              className="rounded-xl border p-4 text-center" style={{borderColor:`${r.fill}44`,background:`linear-gradient(135deg,${r.fill}11 0%,rgba(15,23,42,.8) 100%)`}}>
              <p className="font-black text-4xl mb-1" style={{color:r.fill}}>
                <CountUp end={r.count} duration={1.5} preserveValue/>
              </p>
              <p className="text-slate-300 text-sm font-semibold">{r.risk}</p>
              <p className="text-slate-500 text-xs mt-1">véhicules à risque 14j</p>
              {r.risk==='CRITIQUE'&&r.count>0&&(
                <motion.div className="mt-2 text-xs text-red-300 font-medium"
                  animate={{opacity:[1,.5,1]}} transition={{duration:1,repeat:Infinity}}>
                  Action préventive requise
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
        <p className="text-slate-500 text-xs mt-3 pt-3 border-t border-blue-900/30">
          Modèle ML entraîné sur: historique consommation · patterns fraude · maintenance · GPS trips · transactions carte carburant
        </p>
      </Card>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Immat. / station…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:fraudF,s:setFraudF,o:['CONFIRMÉ','SUSPECT','NON'],p:'Fraude'},
          {v:riskF,s:setRiskF,o:['CRITIQUE','ÉLEVÉ','FAIBLE'],p:'Risque 14j'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        <button onClick={()=>setSortDesc(s=>!s)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white">
          Score ML {sortDesc?<ChevronDown className="w-3 h-3"/>:<ChevronUp className="w-3 h-3"/>}
        </button>
        {(search||fraudF||riskF)&&<button onClick={()=>{setSearch('');setFraudF('');setRiskF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} transactions</span>
      </div>

      {/* Full table */}
      <Card icon={Eye} title="Registre transactions carburant + scores ML — Google Sheets live" accent="text-orange-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight:'400px'}}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['ID','Véhicule','Type','Officier','Date','Station','Litres achetés','Litres attendus','Distance','Cons. réelle','Cons. std','Variance','GPS station','Reçu OK','Score ML','Catégorie','Fraude','Risque 14j','Action'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r,i)=>(
                  <motion.tr key={r.transaction_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                    whileHover={{backgroundColor:'rgba(59,130,246,.05)'}}
                    className={`border-b border-blue-900/30 hover:bg-slate-700/50 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.transaction_id}</td>
                    <td className="px-3 py-2 text-white font-bold">{r.vehicle_registration}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.vehicle_type}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.assigned_officer}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.fuel_date}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-40 truncate">{r.fuel_station}</td>
                    <td className="px-3 py-2 text-right text-blue-300 font-mono">{r.litres_purchased}L</td>
                    <td className="px-3 py-2 text-right text-slate-400 font-mono">{r.litres_expected_for_distance}L</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.distance_km_since_last_fill}km</td>
                    <td className="px-3 py-2 text-center"><span className={r.consumption_actual_l_100km==='N/A'?'text-red-400 font-bold':n(r.consumption_actual_l_100km)>r.consumption_standard_l_100km*1.3?'text-red-400':'text-green-400'}>{r.consumption_actual_l_100km}</span></td>
                    <td className="px-3 py-2 text-center text-slate-400">{r.consumption_standard_l_100km}</td>
                    <td className="px-3 py-2 text-center"><span className={r.consumption_variance_pct==='N/A'?'text-red-400 font-bold':n(r.consumption_variance_pct)>30?'text-red-400 font-bold':n(r.consumption_variance_pct)>15?'text-orange-400':'text-green-400'}>{r.consumption_variance_pct==='N/A'?'N/A':r.consumption_variance_pct+'%'}</span></td>
                    <td className="px-3 py-2 text-center"><span className={r.gps_matches_station==='OUI'?'text-green-400':'text-red-400 font-bold'}>{r.gps_matches_station==='OUI'?'✓':'✗'}</span></td>
                    <td className="px-3 py-2 text-center"><span className={r.receipt_verified==='OUI'?'text-green-400':'text-red-400 font-bold'}>{r.receipt_verified==='OUI'?'✓':'✗'}</span></td>
                    <td className="px-3 py-2 text-center">
                      <div className="relative w-9 h-9 mx-auto">
                        <svg viewBox="0 0 36 36" className="-rotate-90 w-9 h-9">
                          <circle cx="18" cy="18" r="14" fill="none" stroke="#1e293b" strokeWidth="3"/>
                          <motion.circle cx="18" cy="18" r="14" fill="none"
                            stroke={r.anomaly_score_ml>=75?'#ef4444':r.anomaly_score_ml>=55?'#f97316':'#10b981'}
                            strokeWidth="3" initial={{strokeDasharray:'0 88'}}
                            animate={{strokeDasharray:`${r.anomaly_score_ml*.88} 88`}}
                            transition={{duration:1.2,ease:'easeOut',delay:i*.015}} strokeLinecap="round"/>
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">{Math.round(r.anomaly_score_ml)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-orange-300 max-w-44 truncate text-xs">{r.anomaly_category}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                        r.fraud_flag==='CONFIRMÉ'?'bg-red-500/10 border-red-500/30 text-red-400':
                        r.fraud_flag==='SUSPECT'?'bg-orange-500/10 border-orange-500/30 text-orange-400':
                        'bg-green-500/10 border-green-500/30 text-green-400'}`}
                        animate={r.fraud_flag==='CONFIRMÉ'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                        {r.fraud_flag}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap"><span className={`text-xs font-bold ${r.predicted_theft_risk_14d==='CRITIQUE'?'text-red-400':r.predicted_theft_risk_14d==='ÉLEVÉ'?'text-orange-400':'text-green-400'}`}>{r.predicted_theft_risk_14d}</span></td>
                    <td className="px-3 py-2 text-slate-300 max-w-48 truncate text-xs">{r.action_requise}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export const LogisticsAdvancedTabs = () => {
  const [tab, setTab] = useState<'geofencing'|'fuel'>('geofencing');

  const TABS = [
    {k:'geofencing', label:'Géofencing & Détection déplacements', icon:Navigation, accent:'text-green-400'},
    {k:'fuel',       label:'Détection vol carburant IA',           icon:Flame,      accent:'text-orange-400'},
  ];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-blue-900/40"/>
        <span className="text-slate-400 text-xs font-medium px-3">Modules avancés — Intelligence Logistique · Google Sheets live</span>
        <div className="flex-1 h-px bg-blue-900/40"/>
      </div>

      <div className="flex items-center gap-1 rounded-xl p-1 flex-wrap"
        style={{background:'linear-gradient(135deg,rgba(30,41,59,.8) 0%,rgba(15,23,42,.6) 100%)',backdropFilter:'blur(8px)',border:'1px solid rgba(59,130,246,.15)'}}>
        {TABS.map(t=>(
          <motion.button key={t.k} onClick={()=>setTab(t.k as any)}
            whileHover={{scale:1.03}} whileTap={{scale:.97}}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab===t.k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab==='geofencing'&&<motion.div key="g" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><GeofencingTab/></motion.div>}
        {tab==='fuel'      &&<motion.div key="f" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><FuelTheftTab/></motion.div>}
      </AnimatePresence>
    </div>
  );
};
